import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, uploadFile, mediaSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ImageUploader from "@/components/Common/ImageUploader";
import { Video } from "lucide-react";

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const { shop } = useAuth();
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", price: "", wholesale_price: "",
    stock: 1, category: "mode", subcategory: "", tags: "",
    condition: "neuf", wholesale: false,
    images: [], video: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/meta/categories").then(r => setCats(r.data || [])); }, []);

  useEffect(() => {
    if (!form.category) { setSubs([]); return; }
    api.get("/meta/subcategories", { params: { parent: form.category } }).then(r => setSubs(r.data || [])).catch(() => setSubs([]));
  }, [form.category]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/products/${id}`).then(r => {
      const p = r.data;
      setForm({
        title: p.title || "", description: p.description || "",
        price: p.price || "", wholesale_price: p.wholesale_price || "",
        stock: p.stock || 1, category: p.category || "mode", subcategory: p.subcategory || "",
        tags: (p.tags || []).join(", "),
        condition: p.condition || "neuf", wholesale: !!p.wholesale,
        images: p.images || [], video: p.video || null,
      });
    });
  }, [id, isEdit]);

  const uploadVideo = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Vidéo trop lourde (max 15 Mo). Compressez-la ou réduisez la durée.");
      return;
    }
    try {
      const url = await uploadFile(file, "product_video");
      setForm(f => ({ ...f, video: url }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'envoi de la vidéo");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!shop) { toast.error("Créez d'abord votre boutique"); nav("/vendre"); return; }
    if (!form.images.length) { toast.error("Au moins 1 image est requise"); return; }
    setLoading(true);
    const payload = {
      ...form,
      price: parseInt(form.price) || 0,
      wholesale_price: form.wholesale_price ? parseInt(form.wholesale_price) : null,
      stock: parseInt(form.stock) || 1,
      subcategory: form.subcategory || null,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
    };
    try {
      if (isEdit) {
        await api.patch(`/products/${id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/products", payload);
        toast.success("Produit publié !");
      }
      nav("/tableau-bord");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6">
      <h1 className="font-[Manrope] font-extrabold text-2xl">{isEdit ? "Modifier le produit" : "Publier un produit"}</h1>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="product-form">
        <div>
          <Label>Photos * (5 photos maximum)</Label>
          <div className="mt-2">
            <ImageUploader value={form.images} onChange={(imgs) => setForm(f => ({ ...f, images: imgs }))} max={5} />
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-2"><Video className="h-4 w-4" /> Vidéo (optionnel — booste votre produit)</Label>
          {form.video ? (
            <div className="mt-2 relative">
              <video src={mediaSrc(form.video)} controls className="w-full rounded-xl max-h-60 bg-black" />
              <button type="button" onClick={() => setForm(f => ({ ...f, video: null }))} className="absolute top-2 right-2 h-8 px-3 rounded-lg bg-black/70 text-white text-xs font-semibold" data-testid="remove-video">Retirer</button>
            </div>
          ) : (
            <label className="mt-2 h-20 rounded-xl border-2 border-dashed border-[#E8E6E1] hover:border-[#E65100] flex items-center justify-center text-sm text-[#73706B] hover:text-[#E65100] cursor-pointer transition">
              <Video className="h-5 w-5 mr-2" /> Ajouter une vidéo courte
              <input type="file" accept="video/mp4,video/webm,video/quicktime" hidden onChange={(e) => uploadVideo(e.target.files?.[0])} data-testid="upload-video" />
            </label>
          )}
          <div className="text-[11px] text-[#73706B] mt-1">
            Format accepté : MP4, WebM, MOV · Taille max : 15 Mo · Une courte vidéo (~30s) convertit mieux qu'une longue.
          </div>
        </div>

        <div>
          <Label>Titre *</Label>
          <Input data-testid="product-title" required className="h-12 rounded-xl mt-1" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: iPhone 13 Pro Max 256GB" />
        </div>

        <div>
          <Label>Description</Label>
          <Textarea data-testid="product-description" rows={4} className="rounded-xl mt-1" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Détails du produit, état, accessoires inclus..." />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Prix (FCFA) *</Label>
            <Input data-testid="product-price" required inputMode="numeric" className="h-12 rounded-xl mt-1" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} placeholder="Ex: 150000" />
          </div>
          <div>
            <Label>Prix gros (FCFA)</Label>
            <Input data-testid="product-wholesale-price" inputMode="numeric" className="h-12 rounded-xl mt-1" value={form.wholesale_price} onChange={(e) => setForm(f => ({ ...f, wholesale_price: e.target.value }))} placeholder="Ex: 120000" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Stock *</Label>
            <Input data-testid="product-stock" required type="number" min={0} className="h-12 rounded-xl mt-1" value={form.stock} onChange={(e) => setForm(f => ({ ...f, stock: e.target.value }))} />
          </div>
          <div>
            <Label>État</Label>
            <Select value={form.condition} onValueChange={(v) => setForm(f => ({ ...f, condition: v }))}>
              <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="product-condition"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="neuf">Neuf</SelectItem>
                <SelectItem value="occasion">Occasion</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Catégorie *</Label>
          <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v, subcategory: "" }))}>
            <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="product-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cats.map(c => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {subs.length > 0 && (
          <div>
            <Label>Sous-catégorie</Label>
            <Select value={form.subcategory || "__none__"} onValueChange={(v) => setForm(f => ({ ...f, subcategory: v === "__none__" ? "" : v }))}>
              <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="product-subcategory"><SelectValue placeholder="Optionnel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune</SelectItem>
                {subs.map(s => <SelectItem key={s.slug} value={s.slug}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label>Mots-clés (séparés par une virgule)</Label>
          <Input data-testid="product-tags" className="h-12 rounded-xl mt-1" value={form.tags} onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="Ex: smartphone, apple, neuf" />
        </div>

        <div className="flex items-center justify-between py-1">
          <Label className="text-sm font-semibold">Vente en gros disponible</Label>
          <Switch data-testid="product-wholesale" checked={form.wholesale} onCheckedChange={(v) => setForm(f => ({ ...f, wholesale: v }))} />
        </div>

        <Button type="submit" disabled={loading} data-testid="product-submit" className="w-full h-12 rounded-xl bg-[#E65100] hover:bg-[#C44100] font-bold">
          {loading ? "Publication…" : isEdit ? "Enregistrer" : "Publier le produit"}
        </Button>
      </form>
    </div>
  );
}
